import { useState } from "react";
import { Flex } from "../Flex";
import { Image } from "../Image";
import { HiClipboardList, HiOutlineIdentification } from "react-icons/hi";
import logo from "../../assets/verify-tiller-icon.svg";
import styled, { css } from "styled-components";
import { ROUTE } from "../../routes";
import { space } from "styled-system";
import { BiSolidBinoculars, BiLogOutCircle } from "react-icons/bi";
import { FaCog } from "react-icons/fa";
import { Icon } from "../Icon";
import { AiFillCheckSquare } from "react-icons/ai";
import { useBillingInfo } from "../../data/useBillingInfo";
import { useAuth } from "../../context/AuthContext";
import { Modal } from "../Modal";
import { LogoutConfirmModal } from "../Modals/LogoutConfirmModal";
import { useUnsavedChangesNavigation } from "../../hooks/useUnsavedChangesNavigation";

const styles = css`
  ${space}

  ${({ theme: { radii }, isDisabled }: any) => `
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: 58px;
    min-height: 56px;
    padding: 6px 0;
    border-radius: ${radii[0]};
    text-decoration: none;
    transition: background 0.15s ease;

    ${
      !isDisabled
        ? `
      cursor: pointer;
      :hover {
        background: rgba(241, 244, 248, 0.1);
      }`
        : `
      cursor: not-allowed;
      opacity: 0.4;`
    }
  `}
`;

const StyledButton = styled.div<any>`
  ${styles}
`;

const Label = styled.div`
  font-family: ${({ theme }) => theme.fontFamily};
  margin-top: 4px;
  font-size: 9px;
  line-height: 12px;
  color: rgba(255, 255, 255, 0.9);
  text-align: center;
  width: 100%;
  user-select: none;
`;

const Divider = styled.div`
  width: 42px;
  height: 1px;
  margin: 10px auto;
  background: rgba(255, 255, 255, 0.2);
`;

export const SidebarItem = (props: any) => {
  const { tooltip, IconType, to, onClick, children, label, isDisabled } = props;

  const {
    handleNavigation,
    handleSaveChanges,
    handleLeave,
    showModal,
    setShowModal,
  } = useUnsavedChangesNavigation();

  const icon = IconType ? (
    <Icon Type={IconType} color="white" size="26px" />
  ) : null;

  const content = (
    <>
      {children || icon}
      {label && <Label>{label}</Label>}
    </>
  );

  if (onClick) {
    return (
      <StyledButton
        onClick={isDisabled ? undefined : onClick}
        isDisabled={isDisabled}
        title={tooltip}
      >
        {content}
      </StyledButton>
    );
  }

  return (
    <>
      <StyledButton
        onClick={isDisabled ? undefined : (e) => handleNavigation(e, to)}
        isDisabled={isDisabled}
        title={tooltip}
      >
        {content}
      </StyledButton>

      {showModal && (
        <Modal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          width="450px"
          title="You have unsaved changes"
          content="You have made changes to your settings that have not been saved. Do you want to save your changes before leaving this page?"
          primaryButtonText="Save"
          primaryButtonVariant="primary"
          secondaryButtonText="Leave"
          secondaryButtonVariant="danger-text"
          hasSecondaryActionButton
          onModalConfirmClick={handleSaveChanges}
          onSecondaryButtonClick={handleLeave}
        />
      )}
    </>
  );
};

const Sidebar = () => {
  const { data: billingData } = useBillingInfo();
  const { hasPermission, isAdmin } = useAuth();
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);

  const profilesEnabled = !!billingData?.data?.results?.[0]?.profiles_enabled;
  const applicationsEnabled =
    !!billingData?.data?.results?.[0]?.applications_enabled;
  const screeningEnabled = !!billingData?.data?.results?.[0]?.screening_enabled;
  const monitoringEnabled =
    !!billingData?.data?.results?.[0]?.monitoring_enabled;

  const hasAnyPostProfileItem =
    applicationsEnabled || screeningEnabled || monitoringEnabled;
  const showProfilesDivider = profilesEnabled && hasAnyPostProfileItem;

  return (
    <Flex
      // Full viewport height from the very top: the logo tile is the
      // top-left corner, and the top bar starts to the right of the sidebar
      // rather than above it.
      height="100vh"
      // top="0"
      bg="blue.100"
      width="72px"
      flexDirection="column"
      alignItems="center"
      justifyContent="space-between"
      position="fixed"
    >
      <Flex flexDirection="column" alignItems="center">
        <StyledButton isDisabled mt="0">
          <Image src={logo} alt="Tiller Verify Icon" />
        </StyledButton>

        <Flex flexDirection="column" mt="0" pb="3">
          {profilesEnabled && (
            <SidebarItem
              tooltip={
                hasPermission("profile.view")
                  ? "Profiles"
                  : "You do not have permission to view Profiles"
              }
              label="Profiles"
              IconType={HiOutlineIdentification}
              to={ROUTE.PROFILES}
              mb={2}
              isDisabled={!hasPermission("profile.view")}
            />
          )}

          {showProfilesDivider && <Divider />}

          {applicationsEnabled && (
            <SidebarItem
              tooltip={
                hasPermission("mandate.view")
                  ? "Applications"
                  : "You do not have permission to view Applications"
              }
              label="Applications"
              IconType={HiClipboardList}
              to={ROUTE.APPLICATIONS}
              mb={2}
              isDisabled={!hasPermission("mandate.view")}
            />
          )}

          {screeningEnabled && (
            <SidebarItem
              tooltip={
                hasPermission("screening.view")
                  ? "Screening"
                  : "You do not have permission to view Screening"
              }
              label="Screening"
              IconType={AiFillCheckSquare}
              to={ROUTE.CHECKS}
              mb={2}
              isDisabled={!hasPermission("screening.view")}
            />
          )}

          {monitoringEnabled && (
            <SidebarItem
              tooltip={
                hasPermission("monitoring.view")
                  ? "Monitoring"
                  : "You do not have permission to view Monitoring"
              }
              label="Monitoring"
              IconType={BiSolidBinoculars}
              to={ROUTE.MONITORING}
              mb={2}
              isDisabled={!hasPermission("monitoring.view")}
            />
          )}
        </Flex>
      </Flex>

      <Flex flexDirection="column" alignItems="center" pb="3">
        {isAdmin && (
          <SidebarItem
            tooltip="Settings"
            IconType={FaCog}
            to={ROUTE.SETTINGS}
            mb={2}
          />
        )}

        <SidebarItem
          tooltip="Logout"
          IconType={BiLogOutCircle}
          onClick={() => setIsLogoutModalOpen(true)}
          mb={0}
        />
      </Flex>

      <LogoutConfirmModal
        isOpen={isLogoutModalOpen}
        onRequestClose={() => setIsLogoutModalOpen(false)}
      />
    </Flex>
  );
};

export default Sidebar;
